import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormData } from '../types/questionnaire';
import { ENDPOINTS, withQuery } from '../shared/endpoints';
import { post } from '../shared/apiClient';
import { HttpError } from '../shared/httpTypes';
import { ArrowLeft, Loader, Calculator, AlertCircle, CheckCircle } from 'lucide-react';

interface TaxCalculationProps {
  formData: FormData | null;
}

interface TaxResult {
  calculation_id?: string;
  timestamp?: string;
  tax_year?: number;
  filing_status?: string;
  country?: string;
  total_tax_liability: number;
  effective_tax_rate?: number;
  marginal_tax_rate?: number;
  total_income?: number;
  taxable_income?: number;
  effective_rate?: number;
  marginal_rate?: number;
  breakdown?: {
    total_income: number;
    adjusted_gross_income: number;
    taxable_income: number;
    income_tax_before_credits: number;
    total_tax_credits: number;
    income_tax_after_credits: number;
    self_employment_tax: number;
    total_tax_liability: number;
    deduction_used: string;
    standard_deduction_amount: number;
    itemized_deduction_amount: number;
  };
  calculation_steps?: Array<{
    step_number: number;
    step_name: string;
    description: string;
    calculation: string;
    amount: number;
    rule_applied?: string;
    form_reference?: string;
  }>;
  tax_brackets_applied?: Array<{
    min_income: number;
    max_income: number | string;
    rate: number;
    taxable_amount: number;
    tax_amount: number;
  }>;
  rules_applied?: Array<{
    rule_id?: string;
    rule_uuid?: string;
    category?: string;
    rule_type?: string;
    description?: string;
  }>;
  rule_explanations?: Record<string, string>;
  credits_breakdown?: {
    child_tax_credit: number;
    earned_income_credit: number;
    education_credits: number;
    dependent_care_credit: number;
    other_credits: number;
  };
  additional_taxes?: {
    amt: number;
    niit: number;
    additional_medicare_tax: number;
    payroll_tax: number;
  };
  summary?: {
    total_steps: number;
    brackets_used: number;
    rules_applied_count: number;
    credits_applied: number;
    calculation_complexity: string;
  };
  steps?: Array<{
    step_number: number;
    step_name: string;
    description: string;
    calculation: string;
    amount: number;
  }>;
}

const TaxCalculation: React.FC<TaxCalculationProps> = ({ formData }) => {
  const navigate = useNavigate();
  const [isCalculating, setIsCalculating] = useState(false);
  const [result, setResult] = useState<TaxResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleCalculate = useCallback(async () => {
    if (!formData) {
      setError('No questionnaire data available. Please complete the questionnaire first.');
      return;
    }

    setIsCalculating(true);
    setError(null);
    setResult(null);

    try {
      // Extract country and tax_year from formData
      const country = (formData as any).country || 'US';
      const taxYear = (formData as any).tax_year || 2024;
      
      // Convert FormData to flat questionnaire answers format
      // The backend will use questionnaire_mappings to convert this to TaxInput
      const questionnaireAnswers: Record<string, any> = {};
      
      // Flatten FormData structure to question IDs
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          questionnaireAnswers[key] = value;
        }
      });
      
      // Add specific mappings for common fields
      if (formData.maritalStatus) {
        questionnaireAnswers.marital_status = formData.maritalStatus;
        questionnaireAnswers.filing_status = formData.maritalStatus;
      }
      
      if (formData.w2Forms && formData.w2Forms.length > 0) {
        questionnaireAnswers.wages = formData.w2Forms.reduce((sum, w2) => sum + (w2.wages || 0), 0);
        questionnaireAnswers.w2_income = questionnaireAnswers.wages;
        questionnaireAnswers.federal_withholding = formData.w2Forms.reduce((sum, w2) => sum + (w2.federalWithheld || 0), 0);
      }
      
      if (formData.int1099Forms && formData.int1099Forms.length > 0) {
        questionnaireAnswers.interest = formData.int1099Forms.reduce((sum, form) => sum + (form.interest || 0), 0);
        questionnaireAnswers.interest_income = questionnaireAnswers.interest;
      }
      
      if (formData.div1099Forms && formData.div1099Forms.length > 0) {
        questionnaireAnswers.dividends = formData.div1099Forms.reduce((sum, form) => sum + (form.ordinaryDividends || 0), 0);
        questionnaireAnswers.qualified_dividends = formData.div1099Forms.reduce((sum, form) => sum + (form.qualifiedDividends || 0), 0);
      }
      
      if (formData.businessIncome) {
        questionnaireAnswers.business_income = (formData.businessIncome.usSources?.income || 0) + (formData.businessIncome.foreignSources?.income || 0);
        questionnaireAnswers.business_expenses = (formData.businessIncome.usSources?.expenses || 0) + (formData.businessIncome.foreignSources?.expenses || 0);
      }
      
      if (formData.useStandardDeduction !== undefined) {
        questionnaireAnswers.use_standard_deduction = formData.useStandardDeduction;
      }
      
      if (formData.itemizedDeductions) {
        questionnaireAnswers.itemized_deductions = formData.itemizedDeductions.total || 0;
      }
      
      if (formData.childTaxCredit) {
        questionnaireAnswers.children = formData.childTaxCredit.qualifyingChildren || 0;
        questionnaireAnswers.qualifying_children = questionnaireAnswers.children;
      }
      
      if (formData.estimatedTaxPayments) {
        questionnaireAnswers.estimated_payments = formData.estimatedTaxPayments;
        questionnaireAnswers.estimated_tax_payments = formData.estimatedTaxPayments;
      }
      
      if (formData.dateOfBirth) {
        const age = new Date().getFullYear() - new Date(formData.dateOfBirth).getFullYear();
        questionnaireAnswers.age = age;
        questionnaireAnswers.taxpayer_age = age;
      }

      // Use the calculate-from-questionnaire endpoint which:
      // 1. Converts questionnaire answers to TaxInput using questionnaire_mappings service
      // 2. Uses ModernTaxCalculationEngine with database-backed rule engine
      // 3. Returns detailed results with rules applied, brackets used, and step-by-step breakdown
      const endpointUrl = withQuery(ENDPOINTS.tax.calculateFromQuestionnaire, {
        country,
        tax_year: taxYear
      });
      
      // Get full URL with base path
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '/api/v1';
      const fullUrl = `${baseUrl}${endpointUrl}`;
      
      console.log('Calling tax calculation endpoint:', fullUrl);
      console.log('Base URL:', baseUrl);
      console.log('Endpoint path:', endpointUrl);
      console.log('Questionnaire answers:', questionnaireAnswers);
      
      const response = await post<Record<string, any>, TaxResult>(
        endpointUrl, // apiClient will prepend baseUrl automatically
        questionnaireAnswers
      );

      setResult(response.data);
    } catch (err) {
      if (err instanceof HttpError) {
        // Handle HTTP errors with detailed information
        let errorMessage = err.message;
        
        if (err.status === 404) {
          const baseUrl = process.env.REACT_APP_API_BASE_URL || '/api/v1';
          const fullEndpoint = `${baseUrl}${ENDPOINTS.tax.calculateFromQuestionnaire}`;
          errorMessage = `Tax calculation endpoint not found (404). Please check that the API server is running and the endpoint is available at: ${fullEndpoint}`;
          console.error('404 Error Details:', {
            baseUrl,
            endpoint: ENDPOINTS.tax.calculateFromQuestionnaire,
            fullUrl: fullEndpoint,
            error: err
          });
        } else if (err.data) {
          if (typeof err.data === 'object' && 'detail' in err.data) {
            const detail = (err.data.detail as any);
            if (typeof detail === 'object' && 'message' in detail) {
              errorMessage = detail.message;
            } else if (typeof detail === 'string') {
              errorMessage = detail;
            }
          }
        }
        
        setError(errorMessage);
        console.error('Tax calculation HTTP error:', {
          status: err.status,
          statusText: err.statusText,
          message: err.message,
          data: err.data,
          endpoint: ENDPOINTS.tax.calculateFromQuestionnaire
        });
      } else {
        setError(err instanceof Error ? err.message : 'An error occurred while calculating taxes.');
        console.error('Tax calculation error:', err);
      }
    } finally {
      setIsCalculating(false);
    }
  }, [formData]);

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Dashboard</span>
        </button>
        <div className="flex items-center space-x-3">
          <Calculator className="w-8 h-8 text-green-600" />
          <h1 className="text-3xl font-bold text-gray-900">Tax Calculation</h1>
        </div>
        <p className="text-gray-600 mt-2">
          Calculate your tax liability based on your questionnaire responses.
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-red-900">Calculation Error</h3>
            <p className="text-red-700 text-sm mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Calculate Button */}
      {!result && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <button
            onClick={handleCalculate}
            disabled={isCalculating || !formData}
            className={`w-full px-6 py-3 rounded-md font-semibold transition-colors flex items-center justify-center space-x-2 ${
              isCalculating || !formData
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            {isCalculating ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                <span>Calculating...</span>
              </>
            ) : (
              <>
                <Calculator className="w-5 h-5" />
                <span>Calculate Tax Liability</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="space-y-6">
          {/* Summary Card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <h2 className="text-2xl font-bold text-gray-900">Tax Calculation Results</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Total Income</p>
                <p className="text-2xl font-bold text-gray-900">
                  ${(result.breakdown?.total_income || result.total_income || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Taxable Income</p>
                <p className="text-2xl font-bold text-gray-900">
                  ${(result.breakdown?.taxable_income || result.taxable_income || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-gray-600 mb-1">Total Tax Liability</p>
                <p className="text-2xl font-bold text-green-700">
                  ${result.total_tax_liability?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                </p>
              </div>
              
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Effective Rate</p>
                <p className="text-2xl font-bold text-blue-700">
                  {(result.effective_tax_rate || result.effective_rate || 0).toFixed(2)}%
                </p>
              </div>
            </div>
            
            {/* Additional Info */}
            {(result.tax_year || result.filing_status || result.country) && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                  {result.tax_year && <span><strong>Tax Year:</strong> {result.tax_year}</span>}
                  {result.filing_status && <span><strong>Filing Status:</strong> {result.filing_status}</span>}
                  {result.country && <span><strong>Country:</strong> {result.country}</span>}
                  {result.summary && (
                    <span><strong>Complexity:</strong> {result.summary.calculation_complexity}</span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Detailed Breakdown */}
          {result.breakdown && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Detailed Breakdown</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Adjusted Gross Income (AGI)</p>
                  <p className="text-xl font-semibold text-gray-900">
                    ${(result.breakdown.adjusted_gross_income || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Income Tax Before Credits</p>
                  <p className="text-xl font-semibold text-gray-900">
                    ${(result.breakdown.income_tax_before_credits || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Total Tax Credits</p>
                  <p className="text-xl font-semibold text-green-700">
                    ${(result.breakdown.total_tax_credits || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Deduction Used</p>
                  <p className="text-xl font-semibold text-gray-900">
                    {result.breakdown.deduction_used || 'Standard'}
                  </p>
                  {result.breakdown.deduction_used === 'Standard' && (
                    <p className="text-xs text-gray-500 mt-1">
                      ${(result.breakdown.standard_deduction_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  )}
                  {result.breakdown.deduction_used === 'Itemized' && (
                    <p className="text-xs text-gray-500 mt-1">
                      ${(result.breakdown.itemized_deduction_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Calculation Steps with Rule References */}
          {(result.calculation_steps || result.steps) && (result.calculation_steps || result.steps)!.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Step-by-Step Calculation
                {result.summary && (
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    ({result.summary.total_steps} steps)
                  </span>
                )}
              </h3>
              <div className="space-y-3">
                {(result.calculation_steps || result.steps)!.map((step, index) => (
                  <div key={index} className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-semibold">
                      {step.step_number}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">{step.step_name}</h4>
                          <p className="text-sm text-gray-600 mt-1">{step.description}</p>
                          {step.calculation && (
                            <p className="text-sm text-gray-500 mt-1 font-mono bg-white px-2 py-1 rounded">
                              {step.calculation}
                            </p>
                          )}
                          {(step as any).rule_applied && (
                            <p className="text-xs text-blue-600 mt-2">
                              <strong>Rule Applied:</strong> {(step as any).rule_applied}
                            </p>
                          )}
                          {(step as any).form_reference && (
                            <p className="text-xs text-gray-500 mt-1">
                              <strong>Form Reference:</strong> {(step as any).form_reference}
                            </p>
                          )}
                        </div>
                        <p className="text-lg font-semibold text-gray-900 ml-4">
                          ${step.amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tax Brackets Applied */}
          {result.tax_brackets_applied && result.tax_brackets_applied.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Tax Brackets Applied
                {result.summary && (
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    ({result.summary.brackets_used} brackets)
                  </span>
                )}
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Income Range</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rate</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Taxable Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tax Amount</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {result.tax_brackets_applied.map((bracket, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">
                          ${bracket.min_income.toLocaleString('en-US')} - {typeof bracket.max_income === 'string' ? bracket.max_income : `$${bracket.max_income.toLocaleString('en-US')}`}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">{bracket.rate}%</td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          ${bracket.taxable_amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                          ${bracket.tax_amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Rules Applied */}
          {result.rules_applied && result.rules_applied.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Tax Rules Applied
                {result.summary && (
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    ({result.summary.rules_applied_count} rules)
                  </span>
                )}
              </h3>
              <div className="space-y-2">
                {result.rules_applied.map((rule, index) => (
                  <div key={index} className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{rule.description || rule.rule_type || 'Tax Rule'}</p>
                        {rule.category && (
                          <p className="text-xs text-gray-600 mt-1">
                            <strong>Category:</strong> {rule.category}
                          </p>
                        )}
                        {rule.rule_id && (
                          <p className="text-xs text-gray-500 mt-1">
                            <strong>Rule ID:</strong> {rule.rule_id}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Rule Explanations */}
          {result.rule_explanations && Object.keys(result.rule_explanations).length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Rule Explanations</h3>
              <div className="space-y-3">
                {Object.entries(result.rule_explanations).map(([key, explanation], index) => (
                  <div key={index} className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <p className="font-medium text-gray-900 mb-1">{key}</p>
                    <p className="text-sm text-gray-700">{explanation}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Credits Breakdown */}
          {result.credits_breakdown && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Tax Credits Breakdown
                {result.summary && (
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    ({result.summary.credits_applied} credits applied)
                  </span>
                )}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {result.credits_breakdown.child_tax_credit > 0 && (
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-sm text-gray-600 mb-1">Child Tax Credit</p>
                    <p className="text-xl font-semibold text-green-700">
                      ${result.credits_breakdown.child_tax_credit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                )}
                {result.credits_breakdown.earned_income_credit > 0 && (
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-sm text-gray-600 mb-1">Earned Income Credit</p>
                    <p className="text-xl font-semibold text-green-700">
                      ${result.credits_breakdown.earned_income_credit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                )}
                {result.credits_breakdown.education_credits > 0 && (
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-sm text-gray-600 mb-1">Education Credits</p>
                    <p className="text-xl font-semibold text-green-700">
                      ${result.credits_breakdown.education_credits.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                )}
                {result.credits_breakdown.dependent_care_credit > 0 && (
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-sm text-gray-600 mb-1">Dependent Care Credit</p>
                    <p className="text-xl font-semibold text-green-700">
                      ${result.credits_breakdown.dependent_care_credit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                )}
                {result.credits_breakdown.other_credits > 0 && (
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-sm text-gray-600 mb-1">Other Credits</p>
                    <p className="text-xl font-semibold text-green-700">
                      ${result.credits_breakdown.other_credits.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Additional Taxes */}
          {result.additional_taxes && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Additional Taxes</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {result.additional_taxes.amt > 0 && (
                  <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <p className="text-sm text-gray-600 mb-1">Alternative Minimum Tax (AMT)</p>
                    <p className="text-xl font-semibold text-yellow-700">
                      ${result.additional_taxes.amt.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                )}
                {result.additional_taxes.niit > 0 && (
                  <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <p className="text-sm text-gray-600 mb-1">Net Investment Income Tax (NIIT)</p>
                    <p className="text-xl font-semibold text-yellow-700">
                      ${result.additional_taxes.niit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                )}
                {result.additional_taxes.additional_medicare_tax > 0 && (
                  <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <p className="text-sm text-gray-600 mb-1">Additional Medicare Tax</p>
                    <p className="text-xl font-semibold text-yellow-700">
                      ${result.additional_taxes.additional_medicare_tax.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                )}
                {result.additional_taxes.payroll_tax > 0 && (
                  <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <p className="text-sm text-gray-600 mb-1">Payroll Tax</p>
                    <p className="text-xl font-semibold text-yellow-700">
                      ${result.additional_taxes.payroll_tax.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Recalculate Button */}
          <div className="flex justify-center">
            <button
              onClick={handleCalculate}
              disabled={isCalculating}
              className="px-6 py-3 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors disabled:opacity-50"
            >
              Recalculate
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaxCalculation;

